import React, { useState, useEffect, useRef } from 'react';
import Header from '../components/Header';
import Footer from '../components/footer';
import { projectFirestore, timestamp } from '../firebase/config';
import { CLOUDINARY_CLOUD_NAME, CLOUDINARY_UPLOAD_PRESET } from '../cloudinary/config';
import useAIImages from '../hooks/useAIImages';
import { getThumbUrl } from '../utils/imageHelper';
import { useAuth } from '../contexts/AuthContext';
import { enhancePrompt, DEFAULT_LLM_PROVIDERS, DEFAULT_SYSTEM_PROMPT } from '../services/llmService';

// الخدمات المجانية الافتراضية
const DEFAULT_PROVIDERS = [
    { id: 'flux-schnell', name: 'FLUX.1 Schnell', spaceId: 'black-forest-labs/FLUX.1-schnell', endpoint: '/infer', enabled: true, order: 0 },
    { id: 'flux-dev', name: 'FLUX.1 Dev', spaceId: 'black-forest-labs/FLUX.1-dev', endpoint: '/infer', enabled: true, order: 1 },
    { id: 'shuttle-aesthetic', name: 'Shuttle 3.1 Aesthetic', spaceId: 'shuttleai/shuttle-3.1-aesthetic', endpoint: '/infer', enabled: true, order: 2 },
];

// مزودات احتياطية مجانية لتخفيف الضغط عند تعطل HF Spaces
const EXTRA_FALLBACK_PROVIDERS = [
    { id: 'pollinations-flux', name: 'Pollinations (Flux)', type: 'pollinations', model: 'flux' },
    { id: 'pollinations-turbo', name: 'Pollinations (Turbo)', type: 'pollinations', model: 'turbo' },
    { id: 'pollinations-sdxl', name: 'Pollinations (SDXL)', type: 'pollinations', model: 'sdxl' },
    { id: 'pollinations-openjourney', name: 'Pollinations (OpenJourney)', type: 'pollinations', model: 'openjourney' },
];

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// معرف مجهول للمستخدمين غير المسجلين
function getAnonId() {
    let id = localStorage.getItem('ai_anon_id');
    if (!id) {
        id = 'anon_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
        localStorage.setItem('ai_anon_id', id);
        // حفظ في كوكي أيضاً
        document.cookie = 'ai_anon_id=' + id + ';max-age=31536000;path=/;SameSite=Lax';
    }
    return id;
}

function AIGenerator() {
    const [prompt, setPrompt] = useState('');
    const [generating, setGenerating] = useState(false);
    const [generatedImg, setGeneratedImg] = useState(null);
    const [error, setError] = useState('');
    const [progress, setProgress] = useState('');
    const [previewImg, setPreviewImg] = useState(null);
    const abortRef = useRef(null);
    const enhanceAbortRef = useRef(null);
    const { images: galleryImages, loading: galleryLoading, error: galleryError } = useAIImages();

    // إعدادات AI
    const [aiConfig, setAiConfig] = useState(null);
    const [configLoading, setConfigLoading] = useState(true);

    // حالة تحسين البرومبت
    const [enhancedPrompt, setEnhancedPrompt] = useState('');
    const [isEnhancing, setIsEnhancing] = useState(false);
    const [enhanceError, setEnhanceError] = useState('');
    const [moderationStatus, setModerationStatus] = useState(''); // 'safe' | 'blocked' | ''
    const [llmConfig, setLlmConfig] = useState(null);
    const [apiKeys, setApiKeys] = useState({});
    const [connectionError, setConnectionError] = useState('');

    // Auth للتحقق من تسجيل الدخول
    const { user } = useAuth() || {};

    useEffect(() => {
        const unsub = projectFirestore.collection('settings').doc('ai')
            .onSnapshot((doc) => {
                if (doc.exists) {
                    const data = doc.data();
                    // إذا لم تكن هناك خدمات محفوظة، استخدم الافتراضية
                    if (!data.providers || data.providers.length === 0) {
                        data.providers = DEFAULT_PROVIDERS;
                    }
                    setAiConfig(data);
                } else {
                    // أول مرة — إنشاء إعدادات افتراضية
                    const defaultConfig = {
                        enabled: true,
                        requireLogin: false,
                        maxPerDay: 10,
                        providers: DEFAULT_PROVIDERS,
                    };
                    projectFirestore.collection('settings').doc('ai').set(defaultConfig);
                    setAiConfig(defaultConfig);
                }
                setConfigLoading(false);
                setConnectionError('');
            }, (err) => {
                console.error('AI settings listener error:', err);
                setConfigLoading(false);
                setConnectionError('تعذر الاتصال بخدمة الإعدادات. تحقق من الشبكة ثم أعد المحاولة.');
            });
        return () => unsub();
    }, []);

    // تحميل إعدادات LLM (الشات بوت)
    useEffect(() => {
        const unsub1 = projectFirestore.collection('settings').doc('ai-llm')
            .onSnapshot((doc) => {
                if (doc.exists) {
                    setLlmConfig(doc.data());
                } else {
                    setLlmConfig({
                        enabled: true,
                        systemPrompt: DEFAULT_SYSTEM_PROMPT,
                        providers: DEFAULT_LLM_PROVIDERS,
                    });
                }
            }, (err) => {
                console.error('LLM settings listener error:', err);
                setConnectionError('تعذر الاتصال بإعدادات الذكاء الاصطناعي.');
            });
        const unsub2 = projectFirestore.collection('settings').doc('ai-keys')
            .onSnapshot((doc) => {
                if (doc.exists) setApiKeys(doc.data());
            }, (err) => {
                console.error('AI keys listener error:', err);
                setConnectionError('تعذر تحميل مفاتيح الخدمات حالياً بسبب مشكلة اتصال.');
            });
        return () => { unsub1(); unsub2(); };
    }, []);

    // الحصول على الخدمات المفعلة مرتبة
    const getEnabledProviders = () => {
        if (!aiConfig || !aiConfig.providers) return [];
        return aiConfig.providers
            .filter(p => p.enabled !== false)
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    };

    // التحقق من حدود الاستخدام
    const checkRateLimit = async () => {
        const maxPerDay = aiConfig?.maxPerDay || 10;
        const userId = user ? user.uid : getAnonId();
        const docId = user ? userId : userId;
        const today = new Date().toISOString().split('T')[0];

        try {
            const rateLimitDoc = await projectFirestore.collection('ai-rate-limits').doc(docId).get();
            if (rateLimitDoc.exists) {
                const data = rateLimitDoc.data();
                if (data.date === today && data.count >= maxPerDay) {
                    return { allowed: false, remaining: 0, limit: maxPerDay };
                }
                const currentCount = data.date === today ? data.count : 0;
                return { allowed: true, remaining: maxPerDay - currentCount, limit: maxPerDay };
            }
            return { allowed: true, remaining: maxPerDay, limit: maxPerDay };
        } catch (err) {
            console.warn('Rate limit check failed:', err);
            return { allowed: true, remaining: maxPerDay, limit: maxPerDay };
        }
    };

    // تحديث عداد الاستخدام
    const incrementRateLimit = async () => {
        const userId = user ? user.uid : getAnonId();
        const today = new Date().toISOString().split('T')[0];

        try {
            const ref = projectFirestore.collection('ai-rate-limits').doc(userId);
            const doc = await ref.get();
            if (doc.exists && doc.data().date === today) {
                await ref.update({ count: (doc.data().count || 0) + 1 });
            } else {
                await ref.set({ count: 1, date: today, uid: userId, type: user ? 'user' : 'anonymous' });
            }
        } catch (err) {
            console.warn('Rate limit update failed:', err);
        }
    };

    // رفع الصورة إلى Cloudinary
    const uploadToCloudinary = async (blob) => {
        const url = 'https://api.cloudinary.com/v1_1/' + CLOUDINARY_CLOUD_NAME + '/image/upload';
        const formData = new FormData();
        formData.append('file', blob);
        formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
        formData.append('folder', 'ai-generated');
        const response = await fetch(url, { method: 'POST', body: formData });
        if (!response.ok) throw new Error('فشل رفع الصورة إلى Cloudinary');
        const data = await response.json();
        return data.secure_url;
    };

    // تحويل أي صيغة Space إلى رابط تشغيل hf.space
    const resolveSpaceBaseUrl = (spaceId) => {
        let raw = String(spaceId || '').trim();
        if (!raw) return '';

        // إذا تم إدخال رابط hf.space مباشر
        if (raw.includes('hf.space')) {
            try {
                const u = new URL(raw.startsWith('http') ? raw : 'https://' + raw);
                return u.origin;
            } catch (e) {
                return '';
            }
        }

        // إذا تم إدخال رابط huggingface.co/spaces/owner/name
        const hfMatch = raw.match(/huggingface\.co\/spaces\/([^/\s]+)\/([^/\s?#]+)/i);
        if (hfMatch) {
            raw = hfMatch[1] + '/' + hfMatch[2];
        }

        raw = raw.replace(/^spaces\//i, '').replace(/^\/+|\/+$/g, '');
        const parts = raw.split('/').filter(Boolean);
        if (parts.length < 2) return '';

        const owner = parts[parts.length - 2];
        const name = parts[parts.length - 1];
        // HF Spaces تحول النقاط والشرطات السفلية إلى شرطات في الـ subdomain
        const subdomain = (owner + '-' + name).replace(/[._]+/g, '-').toLowerCase();
        return 'https://' + subdomain + '.hf.space';
    };

    // توليد صورة من خدمة Gradio واحدة
    const generateFromProvider = async (provider, promptText, signal) => {
        // مزود بديل لا يعتمد على HF Spaces
        if (provider.type === 'pollinations') {
            setProgress('جاري الاتصال بـ ' + provider.name + '...');
            let lastErr = null;
            for (let attempt = 0; attempt < 3; attempt += 1) {
                const imageUrl = 'https://image.pollinations.ai/prompt/' + encodeURIComponent(promptText)
                    + '?width=768&height=768&nologo=true&safe=true&seed=' + (Date.now() + attempt)
                    + '&model=' + encodeURIComponent(provider.model || 'flux');
                const imgRes = await fetch(imageUrl, { signal, cache: 'no-store' });
                if (imgRes.ok) {
                    const imageBlob = await imgRes.blob();
                    if (!imageBlob || imageBlob.size === 0) {
                        throw new Error(provider.name + ': تم استلام استجابة فارغة');
                    }
                    return { blob: imageBlob, providerName: provider.name };
                }

                if (imgRes.status === 429) {
                    lastErr = new Error(provider.name + ': ضغط مرتفع على الخدمة (429)');
                    if (attempt < 2) {
                        setProgress(provider.name + ': الخدمة مزدحمة، إعادة المحاولة...');
                        await sleep(1000 * (attempt + 1));
                        continue;
                    }
                }
                const errText = await imgRes.text().catch(() => '');
                throw new Error(provider.name + ': خطأ ' + imgRes.status + ' أثناء توليد الصورة ' + errText.slice(0, 120));
            }
            throw lastErr || new Error(provider.name + ': فشل غير متوقع أثناء توليد الصورة');
        }

        const baseUrl = resolveSpaceBaseUrl(provider.spaceId);
        if (!baseUrl) {
            throw new Error(provider.name + ': Space ID غير صالح. استخدم owner/name أو رابط Space كامل.');
        }
        const endpoint = (provider.endpoint || '/infer').startsWith('/')
            ? (provider.endpoint || '/infer')
            : '/' + (provider.endpoint || '/infer');

        // Step 1: إرسال الطلب
        setProgress('جاري الاتصال بـ ' + provider.name + '...');
        const submitRes = await fetch(baseUrl + '/gradio_api/call' + endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ data: [promptText, 0, true, 1024, 1024, 4] }),
            signal,
        });

        if (!submitRes.ok) {
            const errText = await submitRes.text().catch(() => '');
            if (submitRes.status === 404) {
                throw new Error(provider.name + ': 404 من Hugging Face. تأكد من Space ID و endpoint في لوحة الإدارة.');
            }
            throw new Error(provider.name + ': خطأ ' + submitRes.status + ' — ' + errText.slice(0, 200));
        }

        const submitData = await submitRes.json();
        const eventId = submitData.event_id;
        if (!eventId) throw new Error(provider.name + ': لم يتم الحصول على معرف الطلب');

        // Step 2: متابعة التقدم عبر SSE
        setProgress('في قائمة الانتظار (' + provider.name + ')...');
        const streamRes = await fetch(baseUrl + '/gradio_api/call' + endpoint + '/' + eventId, { signal });
        if (!streamRes.ok) {
            const errText = await streamRes.text().catch(() => '');
            throw new Error(provider.name + ': فشل متابعة التقدم (' + streamRes.status + ') ' + errText.slice(0, 200));
        }
        if (!streamRes.body) {
            throw new Error(provider.name + ': لم تصل بيانات البث من الخدمة');
        }
        const reader = streamRes.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let imagePath = null;

        while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
                if (line.startsWith('event:')) {
                    const evt = line.replace('event:', '').trim();
                    if (evt === 'heartbeat') continue;
                    if (evt === 'error') {
                        const dataLine = lines.find(l => l.startsWith('data:'));
                        const errText = (dataLine || '').slice(5, 300).trim();
                        throw new Error(provider.name + ': ' + (errText && errText !== 'null' ? errText : 'استجابة فارغة من الخدمة (null)'));
                    }
                }
                if (line.startsWith('data:')) {
                    try {
                        const raw = line.slice(5).trim();
                        if (!raw || raw === 'null') continue;
                        const d = JSON.parse(raw);
                        // التقدم: رقم أو مصفوفة
                        if (typeof d === 'number') {
                            setProgress(provider.name + ': ' + Math.round(d * 100) + '%');
                        } else if (Array.isArray(d) && typeof d[0] === 'number') {
                            setProgress(provider.name + ': خطوة ' + d[0] + '/' + (d[1] || '?'));
                        } else if (Array.isArray(d)) {
                            // النتيجة
                            const output = d[0];
                            imagePath = typeof output === 'string' ? output
                                : (output && output.url) ? output.url
                                : (output && output.path) ? output.path
                                : null;
                        } else if (d && typeof d === 'object') {
                            if (d.url) imagePath = d.url;
                            else if (d.path) imagePath = d.path;
                        }
                    } catch (e) { /* ليس JSON */ }
                }
            }
        }

        if (!imagePath) throw new Error(provider.name + ': لم يتم الحصول على صورة من الاستجابة (الخدمة قد تكون مزدحمة حالياً)');

        // Step 3: تحميل الصورة
        setProgress('جاري تحميل الصورة من ' + provider.name + '...');
        const imageUrl = imagePath.startsWith('http') ? imagePath : baseUrl + '/gradio_api/file=' + imagePath;
        const imgRes = await fetch(imageUrl, { signal });
        if (!imgRes.ok) throw new Error(provider.name + ': فشل تحميل ملف الصورة');
        const imageBlob = await imgRes.blob();

        return { blob: imageBlob, providerName: provider.name };
    };

    // تحسين البرومبت بالذكاء الاصطناعي
    const handleEnhance = async () => {
        if (!prompt.trim()) return;
        setIsEnhancing(true);
        setEnhanceError('');
        setModerationStatus('');
        setEnhancedPrompt('');

        const controller = new AbortController();
        enhanceAbortRef.current = controller;

        try {
            const providers = llmConfig?.providers || DEFAULT_LLM_PROVIDERS;
            const systemPrompt = llmConfig?.systemPrompt || DEFAULT_SYSTEM_PROMPT;
            const result = await enhancePrompt(prompt.trim(), providers, apiKeys, systemPrompt, controller.signal);

            if (result.blocked) {
                setModerationStatus('blocked');
                setEnhanceError('⛔ ' + result.reason + (result.provider ? ' (' + result.provider + ')' : ''));
            } else if (result.success) {
                setEnhancedPrompt(result.prompt);
                setModerationStatus('safe');
            } else {
                setEnhanceError(result.reason || 'فشل تحسين البرومبت');
            }
        } catch (err) {
            if (err.name !== 'AbortError') {
                setEnhanceError('خطأ: ' + err.message);
            }
        }
        setIsEnhancing(false);
    };

    // توليد مع التبديل التلقائي بين الخدمات (+ تحسين تلقائي إذا البوت مفعّل)
    const handleGenerate = async () => {
        if (!prompt.trim() && !enhancedPrompt.trim()) return;
        if (!aiConfig || aiConfig.enabled === false) { setError('خدمة التوليد معطلة حالياً.'); return; }
        if (aiConfig.requireLogin && !user) { setError('يجب تسجيل الدخول لاستخدام خدمة توليد الصور.'); return; }

        const rateCheck = await checkRateLimit();
        if (!rateCheck.allowed) {
            setError('لقد وصلت للحد الأقصى اليوم (' + rateCheck.limit + ' صور). حاول مرة أخرى غداً.');
            return;
        }

        const configuredProviders = getEnabledProviders();
        const providers = [...configuredProviders, ...EXTRA_FALLBACK_PROVIDERS];
        if (providers.length === 0) { setError('لا توجد خدمات مفعلة. يرجى التواصل مع المدير.'); return; }

        setGenerating(true); setError(''); setGeneratedImg(null); setProgress('');

        // تحسين تلقائي للبرومبت إذا البوت مفعّل ولم يتم التحسين مسبقاً
        let finalPrompt = enhancedPrompt.trim();
        if (!finalPrompt && llmConfig?.enabled !== false && prompt.trim()) {
            setProgress('🤖 جاري تحسين الوصف تلقائياً...');
            try {
                const llmProviders = llmConfig?.providers || DEFAULT_LLM_PROVIDERS;
                const systemPrompt = llmConfig?.systemPrompt || DEFAULT_SYSTEM_PROMPT;
                const result = await enhancePrompt(prompt.trim(), llmProviders, apiKeys, systemPrompt);
                if (result.blocked) {
                    setModerationStatus('blocked');
                    setError('⛔ ' + result.reason + (result.provider ? ' (' + result.provider + ')' : ''));
                    setGenerating(false);
                    return;
                }
                if (result.success && result.prompt) {
                    finalPrompt = result.prompt;
                    setEnhancedPrompt(result.prompt);
                    setModerationStatus('safe');
                }
            } catch (err) {
                console.warn('Auto-enhance failed, using original prompt:', err.message);
            }
        }
        // إذا فشل التحسين أو البوت معطّل، استخدم البرومبت الأصلي
        if (!finalPrompt) finalPrompt = prompt.trim();
        let success = false;
        const errors = [];
        let pollinationsQueueFull = false;

        for (const provider of providers) {
            const providerController = new AbortController();
            abortRef.current = providerController;
            let timeoutId = null;
            try {
                // حد زمني 90 ثانية لكل خدمة
                timeoutId = setTimeout(() => {
                    providerController.abort();
                }, 90000);

                const result = await generateFromProvider(provider, finalPrompt, providerController.signal);
                clearTimeout(timeoutId);
                timeoutId = null;

                // رفع إلى Cloudinary
                setProgress('جاري رفع الصورة...');
                const permanentUrl = await uploadToCloudinary(result.blob);

                // حفظ في Firestore
                await projectFirestore.collection('ai-images').add({
                    url: permanentUrl,
                    prompt: finalPrompt,
                    originalPrompt: prompt.trim(),
                    provider: result.providerName,
                    createdAt: timestamp(),
                    userId: user ? user.uid : getAnonId(),
                });

                await incrementRateLimit();
                setGeneratedImg(permanentUrl);
                setPrompt('');
                setEnhancedPrompt('');
                setModerationStatus('');
                setProgress('');
                success = true;
                break;
            } catch (err) {
                if (timeoutId) clearTimeout(timeoutId);
                if (err.name === 'AbortError') {
                    errors.push(provider.name + ': انتهت المهلة');
                } else {
                    const msg = err && err.message
                        ? err.message
                        : (typeof err === 'string' && err.trim() ? err : 'فشل غير معروف');
                    const normalizedMsg = msg === 'null'
                        ? (provider.name + ': الخدمة أعادت استجابة فارغة (null). حاول مرة أخرى أو استخدم مزوداً آخر.')
                        : msg;
                    errors.push(normalizedMsg);

                    // Pollinations المجاني يسمح بطابور محدود جداً لكل IP، لا فائدة من تجربة كل موديل فوراً
                    if (provider.type === 'pollinations' && (normalizedMsg.includes('429') || normalizedMsg.toLowerCase().includes('queue full'))) {
                        pollinationsQueueFull = true;
                        errors.push('ملاحظة: مزود Pollinations مزدحم حالياً لنفس الـ IP. انتظر 30-90 ثانية ثم أعد المحاولة.');
                        break;
                    }
                }
                console.warn('Provider failed:', provider.name, err);
            }
        }

        if (!success) {
            if (pollinationsQueueFull) {
                setError('الخدمات المجانية مزدحمة الآن (Queue Full). حاول بعد دقيقة تقريباً.\n\n' + errors.join('\n'));
            } else {
                setError('فشلت جميع الخدمات:\n' + errors.join('\n'));
            }
            setProgress('');
        }
        setGenerating(false);
    };

    const cancelGeneration = () => {
        if (abortRef.current) abortRef.current.abort();
        setGenerating(false);
        setProgress('');
    };

    // ===================== RENDER =====================
    const isDisabled = !aiConfig || aiConfig.enabled === false;
    const needsLogin = aiConfig?.requireLogin && !user;

    return (
        <>
            <Header />
            <div className="container my-4" style={{ maxWidth: 900 }}>
                <h2 className="text-center mb-4">🧠 توليد صور بالذكاء الاصطناعي</h2>

                {(connectionError || galleryError) && (
                    <div className="alert alert-warning" role="alert">
                        <strong>مشكلة اتصال بالشبكة:</strong>{' '}
                        {connectionError || 'تعذر تحميل المعرض من قاعدة البيانات حالياً.'}
                        {galleryError && !connectionError ? ' (' + galleryError + ')' : ''}
                        <div className="mt-1">حاول تحديث الصفحة بعد دقيقة أو التبديل إلى شبكة أخرى.</div>
                    </div>
                )}

                {configLoading && (
                    <div className="text-center py-4"><div className="spinner-border text-primary"></div></div>
                )}

                {!configLoading && isDisabled && (
                    <div className="alert alert-warning text-center">
                        خدمة توليد الصور بالذكاء الاصطناعي معطلة حالياً.
                    </div>
                )}

                {!configLoading && !isDisabled && needsLogin && (
                    <div className="alert alert-info text-center">
                        <strong>يجب تسجيل الدخول</strong><br />
                        هذه الخدمة متاحة للمستخدمين المسجلين فقط.
                        <div className="mt-2">
                            <a href="/admin" className="btn btn-primary btn-sm">تسجيل الدخول</a>
                        </div>
                    </div>
                )}

                {!configLoading && !isDisabled && !needsLogin && (
                    <div className="card shadow-sm mb-4">
                        <div className="card-body">
                            {/* <div className="mb-2">
                                <span className="badge bg-success">مجاني بالكامل</span>
                                <span className="text-muted ms-2" style={{ fontSize: 12 }}>
                                    {enabledProviders.length} خدمة متاحة — التبديل تلقائي عند الحاجة
                                </span>
                            </div> */}
                            <div className="mb-3">
                                <label className="form-label fw-bold">وصف الصورة</label>
                                <textarea className="form-control" rows={3} value={prompt} onChange={e => { setPrompt(e.target.value); setEnhancedPrompt(''); setModerationStatus(''); setEnhanceError(''); }}
                                    placeholder="اكتب وصف الصورة هنا..."
                                    disabled={generating || isEnhancing} dir="auto" />
                            </div>

                            {/* إشارة أن التحسين تلقائي */}
                            {llmConfig?.enabled !== false && (
                                <div className="mb-3">
                                    <small className="text-muted">
                                        🤖 سيتم تحسين الوصف تلقائياً عند التوليد
                                    </small>
                                    {!enhancedPrompt && prompt.trim() && (
                                        <button className="btn btn-link btn-sm p-0 ms-2" onClick={handleEnhance}
                                            disabled={isEnhancing || generating || moderationStatus === 'blocked'}>
                                            {isEnhancing ? (
                                                <><span className="spinner-border spinner-border-sm me-1"></span> جاري التحسين...</>
                                            ) : 'معاينة التحسين'}
                                        </button>
                                    )}
                                </div>
                            )}

                            {/* حالة الفلترة */}
                            {moderationStatus === 'blocked' && (
                                <div className="alert alert-danger py-2 mb-3">
                                    {enhanceError}
                                </div>
                            )}
                            {enhanceError && moderationStatus !== 'blocked' && (
                                <div className="alert alert-warning py-2 mb-3">
                                    {enhanceError}
                                </div>
                            )}

                            {enhancedPrompt && (
                                <div className="mb-3 p-3 rounded" style={{ background: '#e8f5e9', border: '1px solid #c8e6c9' }}>
                                    <div className="d-flex justify-content-end mb-1">
                                        <button className="btn btn-sm btn-outline-secondary" onClick={() => { setEnhancedPrompt(''); setModerationStatus(''); }}>✕</button>
                                    </div>
                                    <textarea className="form-control" rows={3} value={enhancedPrompt}
                                        onChange={e => setEnhancedPrompt(e.target.value)}
                                        disabled={generating} dir="ltr" style={{ fontSize: 13 }} />
                                </div>
                            )}

                            <div className="d-flex gap-2">
                                <button className="btn btn-primary btn-lg flex-grow-1" onClick={handleGenerate}
                                    disabled={generating || (!prompt.trim() && !enhancedPrompt.trim()) || moderationStatus === 'blocked'}>
                                    {generating ? (
                                        <><span className="spinner-border spinner-border-sm me-2"></span> {progress || 'جاري التوليد...'}</>
                                    ) : '✨ توليد صورة'}
                                </button>
                                {generating && (
                                    <button className="btn btn-outline-danger btn-lg" onClick={cancelGeneration}>إلغاء</button>
                                )}
                            </div>
                            {generating && progress && (
                                <div className="mt-2">
                                    <div className="progress" style={{ height: 6 }}>
                                        <div className="progress-bar progress-bar-striped progress-bar-animated" style={{ width: '100%' }}></div>
                                    </div>
                                    <small className="text-muted d-block text-center mt-1">{progress}</small>
                                </div>
                            )}

                        </div>
                    </div>
                )}

                {error && <div className="alert alert-danger" style={{ whiteSpace: 'pre-wrap' }}>{error}</div>}

                {generatedImg && (
                    <div className="card shadow-sm mb-4">
                        <div className="card-header text-center fw-bold">الصورة المولّدة</div>
                        <div className="text-center p-3">
                            <img src={generatedImg} alt="AI Generated" style={{ maxWidth: '100%', maxHeight: 500, borderRadius: 8 }} />
                        </div>
                        <div className="card-footer text-center">
                            <a href={generatedImg} download="ai-generated.png" className="btn btn-outline-primary btn-sm" target="_blank" rel="noreferrer">
                                ⬇ تنزيل الصورة
                            </a>
                        </div>
                    </div>
                )}

                <hr className="my-4" />
                <h4 className="text-center mb-3">🖼 معرض الصور المولّدة</h4>
                {galleryLoading ? (
                    <div className="text-center py-3"><div className="spinner-border spinner-border-sm"></div></div>
                ) : galleryImages.length === 0 ? (
                    <p className="text-center text-muted">لا توجد صور مولّدة بعد</p>
                ) : (
                    <div className="row g-3">
                        {galleryImages.map(img => (
                            <div className="col-md-4 col-sm-6" key={img.id}>
                                <div className="card shadow-sm h-100" style={{ cursor: 'pointer' }} onClick={() => setPreviewImg(img.url)}>
                                    <img src={getThumbUrl(img.url, 400)} alt="AI Generated" style={{ width: '100%', height: 200, objectFit: 'cover' }} loading="lazy" />
                                    <div className="card-body p-2">
                                        <small className="badge bg-info" style={{ fontSize: 10 }}>
                                            🤖 مولدة بالذكاء الاصطناعي
                                        </small>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {previewImg && (
                    <div className="d-flex justify-content-center align-items-center"
                        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.85)', zIndex: 9999, cursor: 'pointer' }}
                        onClick={() => setPreviewImg(null)}>
                        <img src={previewImg} alt="" style={{ maxWidth: '90vw', maxHeight: '90vh' }} />
                    </div>
                )}
            </div>
            <Footer />
        </>
    );
}

export default AIGenerator;
